import { Fragment } from 'react'

import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  TabsContent as RawTabsContent,
  TabsList as RawTabsList,
  TabsTrigger as RawTabsTrigger,
  TabsRoot,
  Text,
} from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { BgMasterTab } from '~/pages/AdminOcctoMaster/tabs/BgMaster'
import { BusinessMasterTab } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster'
import { DemandProcurementPlanMasterTab } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster'
import { DemandSuppressionPlanMasterTab } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster'
import { GenerationSalesPlanMasterTab } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster'
import { PlantMasterTab } from '~/pages/AdminOcctoMaster/tabs/PlantMaster'
import { SplitSupplyContractTab } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract'
import { SubmitterMasterTab } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster'
import { TradingPartnerTab } from '~/pages/AdminOcctoMaster/tabs/TradingPartner'
import { routePaths } from '~/routes/routePaths'

/**
 * chakra v3 の `TabsList*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
 * d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop / `value` が
 * 欠落する。 v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する。
 * ランタイムは ecloud のまま (SpotBidding/index.tsx と同型の cast パターン)。
 */
const TabsList = RawTabsList as React.FC<HTMLChakraProps<'div'>>
const TabsTrigger = RawTabsTrigger as React.FC<
  HTMLChakraProps<'button'> & { value: TabKey }
>
const TabsContent = RawTabsContent as React.FC<
  HTMLChakraProps<'div'> & { value: string }
>

// Phase1 で表示するタブ 8 種 (spec `admin/master/occto-master/index.md` AC-MOI-001 /
// 4525-occto-master-sidemenu 設計書)。
// - 容量市場応札 / 容量市場月別アセスメント: Phase2 以降のため除去
// - 電源ユニット: 08/03 spec 改訂で独立メニュー化済み (別 map #3565)、本画面の対象外
//
// タブラベル・説明文・グループ・描画コンポーネントの単一ソース。ラベルはサイドメニュー
// 項目名と右ペイン見出し (h2) の両方が同じキーを参照する (design/admin/occto.html の
// `_updateTabTitle()` が h2 の文言を左メニュー nav ボタンの textContent から取って
// いるのと同じ判断 = ラベルの二重管理を避ける)。説明文は proto `OCCTO_TAB_DESC`
// 実文言を i18n へ移したもの。
type TabGroupKey = 'basicMaster' | 'plan'

type TabDef = {
  key: string
  labelKey: string
  descKey: string
  group: TabGroupKey
  component: React.ComponentType
}

// 並び順 = spec AC-MOI-001 (基本マスタ: 事業者→BG→計画提出者→取引先→発電所→分割供給契約 /
// 計画: 需要調達計画→発電販売計画→需要抑制計画)。TABS の宣言順がそのままサイドメニューの
// 描画順になる (グループ内は宣言順を保つため filter を使う)。
const TABS = [
  {
    key: 'business',
    labelKey: 'page:adminOcctoMaster.tab.business',
    descKey: 'page:adminOcctoMaster.tabDescription.business',
    group: 'basicMaster',
    component: BusinessMasterTab,
  },
  {
    key: 'bg',
    labelKey: 'page:adminOcctoMaster.tab.bg',
    descKey: 'page:adminOcctoMaster.tabDescription.bg',
    group: 'basicMaster',
    component: BgMasterTab,
  },
  {
    key: 'submitter',
    labelKey: 'page:adminOcctoMaster.tab.submitter',
    descKey: 'page:adminOcctoMaster.tabDescription.submitter',
    group: 'basicMaster',
    component: SubmitterMasterTab,
  },
  {
    key: 'trading-partner',
    labelKey: 'page:adminOcctoMaster.tab.tradingPartner',
    descKey: 'page:adminOcctoMaster.tabDescription.tradingPartner',
    group: 'basicMaster',
    component: TradingPartnerTab,
  },
  {
    key: 'plant',
    labelKey: 'page:adminOcctoMaster.tab.plant',
    descKey: 'page:adminOcctoMaster.tabDescription.plant',
    group: 'basicMaster',
    component: PlantMasterTab,
  },
  {
    key: 'split-supply',
    labelKey: 'page:adminOcctoMaster.tab.splitSupply',
    descKey: 'page:adminOcctoMaster.tabDescription.splitSupply',
    group: 'basicMaster',
    component: SplitSupplyContractTab,
  },
  {
    key: 'proc-plan',
    labelKey: 'page:adminOcctoMaster.tab.demandProcurementPartner',
    descKey: 'page:adminOcctoMaster.tabDescription.demandProcurementPartner',
    group: 'plan',
    component: DemandProcurementPlanMasterTab,
  },
  {
    key: 'gen-plan',
    labelKey: 'page:adminOcctoMaster.tab.generationSalesPartner',
    descKey: 'page:adminOcctoMaster.tabDescription.generationSalesPartner',
    group: 'plan',
    component: GenerationSalesPlanMasterTab,
  },
  {
    key: 'supp-plan',
    labelKey: 'page:adminOcctoMaster.tab.demandSuppressionPartner',
    descKey: 'page:adminOcctoMaster.tabDescription.demandSuppressionPartner',
    group: 'plan',
    component: DemandSuppressionPlanMasterTab,
  },
] as const satisfies readonly TabDef[]

type TabKey = (typeof TABS)[number]['key']

const TABS_BY_KEY = Object.fromEntries(
  TABS.map((tab) => [tab.key, tab]),
) as Record<TabKey, (typeof TABS)[number]>

/** サイドメニューのグループ構造 (spec AC-MOI-001 の基本マスタ → 計画)。 */
const TAB_GROUPS: ReadonlyArray<{ key: TabGroupKey; labelKey: string }> = [
  { key: 'basicMaster', labelKey: 'page:adminOcctoMaster.group.basicMaster' },
  { key: 'plan', labelKey: 'page:adminOcctoMaster.group.plan' },
]

const DEFAULT_TAB: TabKey = 'business'

// `in` は Object.prototype 由来のキー (`toString` / `__proto__` 等) も true に
// するため own-key 判定を使う。 `?tab=toString` で guard を通すと
// `TABS_BY_KEY[tab].component` が undefined になり描画が落ちる。
const isTabKey = (value: string | null): value is TabKey =>
  value !== null && Object.hasOwn(TABS_BY_KEY, value)

export const AdminOcctoMasterPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  // 他画面 (需要調達計画 作成ダイアログのエリア候補 0 件案内、issue #6353) から
  // 特定タブへ直接誘導できるよう、?tab=<key> を選択状態の唯一のソースにする。
  // 未指定・不正値は既定 (事業者) にフォールバックする。
  const requestedTab = searchParams.get('tab')
  const tab = isTabKey(requestedTab) ? requestedTab : DEFAULT_TAB
  const activeTab = TABS_BY_KEY[tab]
  const ActiveTabComponent = activeTab.component

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminOcctoMaster.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        { label: t('common:route.adminOccto'), bold: true },
      ]}
    >
      <PageBody>
        {/* design/admin/occto.html `.card.occto-layout` 相当: 白 card 面に
            左固定サイドメニュー + 右コンテンツペインの 2 カラム。 card 外枠は
            `Surface.primary` + `Border.lowEmphasis` の 1px + 角丸 (既存 card 系
            component と揃える。 UnderDevPlaceholder 等と同じ pattern)。 */}
        <Box
          flexDirection="column"
          flex="1"
          display="flex"
          overflow="hidden"
          minHeight={0}
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          backgroundColor="Surface.primary"
        >
          {/* DS TabsComponent (flat items) はグループ見出しの差し込み口・orientation prop を
              持たないため raw slot を使う (ADR frontend-tabs-no-wrapper-closed-allowlist
              の raw slot 例外条件に該当)。orientation="vertical" で自身が flex row
              (サイドメニュー + 右ペイン) になる (chakra tabs recipe の
              root._vertical: display:flex)。 */}
          <TabsRoot
            flex="1"
            minHeight={0}
            onValueChange={(details: { value: string }) => {
              if (!isTabKey(details.value)) return
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev)
                  next.set('tab', details.value)
                  return next
                },
                { replace: true },
              )
            }}
            orientation="vertical"
            value={tab}
          >
            {/* サイドメニュー: design `.occto-menu` 準拠 (幅 210px 固定 /
                `Surface.secondary` の少し落とした面色 / 右境界線 / padding 8px /
                項目間 gap 2px)。 内側の TabsTrigger は DS SideMenuLayoutMenuItem
                (= design `.nav-item-top`) の見た目に完全準拠させる (下記 TabsTrigger
                コメント参照)。 */}
            <TabsList
              flexDirection="column"
              flexShrink={0}
              gap={0.5}
              display="flex"
              overflowY="auto"
              width="210px"
              padding={2}
              borderRightWidth="1px"
              borderRightColor="Border.lowEmphasis"
              /* recipe base の borderBottomWidth 1px (horizontal tab 用のタブ下線)
                 は vertical mode でも残り、 sidemenu 下端に不要な横線が出るため
                 打ち消す。 */
              borderBottomWidth={0}
              backgroundColor="Surface.secondary"
            >
              {TAB_GROUPS.map((group) => (
                <Fragment key={group.key}>
                  {/* 非クリックの静的見出し。 role="tab" を持たせず tablist の意味論
                      から除外する (design `.occto-menu-group-label` 準拠、
                      getAllByRole('tab') は 8 件のまま = AC-02)。
                      base = captionStrong (sm/600) だが design は sm/700 なので
                      fontWeight="bold" で 700 に上書きする。 lint
                      `ebalance/no-adhoc-typography` は fontSize + fontWeight の
                      JSX attribute 併記のみを検出するため、 textStyle + fontWeight
                      の組は対象外 (合法な override 経路)。 */}
                  <Text
                    textStyle="captionStrong"
                    marginTop={1}
                    paddingTop={3}
                    paddingBottom={1}
                    color="Text.midEmphasis"
                    fontWeight="bold"
                    letterSpacing="0.02em"
                    _first={{ marginTop: 0, paddingTop: 1 }}
                    userSelect="none"
                    paddingX={2}
                    role="presentation"
                  >
                    {t(group.labelKey)}
                  </Text>
                  {TABS.filter((tabDef) => tabDef.group === group.key).map(
                    (tabDef) => (
                      // design `.occto-menu .ecl-tab` = DS SideMenuLayoutMenuItem
                      // (`.nav-item-top`) 準拠: min-height 32px / padding 6px 8px /
                      // md (14px) 400 / 折返し可 / border-radius md / 非選択は
                      // Text.lowEmphasis + 透過背景 / hover は Hover.lowEmphasis /
                      // 選択時は Surface.infoLow + Text.accentPrimary + 600。
                      //
                      // DS tabs recipe (ecloud 0.14.2 index.es.js:25100-25165) が
                      // horizontal tab 前提で以下を trigger に付与しており、 sidemenu
                      // 見た目にするには全て打ち消す必要がある (css prop で override):
                      // (a) trigger base = fontWeight 600 (headerMd 由来) → 非選択も
                      //     太字で描画される
                      // (b) & > span = 3px 透過 border-frame + paddingX 2 paddingY 0.5
                      //     + Text.lowEmphasis + radiiMd → 内側に見えない二重枠 + 余白
                      // (c) &[data-selected] = borderBottomWidth 2px + marginBottom
                      //     -1px + paddingBottom 3px + Border.ultraHighEmphasis →
                      //     選択時下線 (design には無い)
                      // (d) &:hover > span = Hover.lowEmphasis → span だけ塗り (私の
                      //     _hover とダブる)
                      // (e) &[data-selected] > span = Text.highEmphasis → 選択色が
                      //     trigger 側の accentPrimary でなく highEmphasis で描画される
                      //
                      // <span> ラッパ自体は ADR frontend-tabs-no-wrapper-closed-allowlist
                      // 準拠で必須 (DS tabs recipe が `& > span` 参照するため)。
                      <TabsTrigger
                        key={tabDef.key}
                        alignItems="center"
                        justifyContent="flex-start"
                        display="flex"
                        width="100%"
                        minHeight="32px"
                        color="Text.lowEmphasis"
                        fontSize="md"
                        textAlign="left"
                        background="transparent"
                        borderRadius="md"
                        _hover={{ backgroundColor: 'Hover.lowEmphasis' }}
                        _focusVisible={{
                          // design layout.css:317 準拠 (3px Focus.clarity)。 2px accent ring は
                          // DS SideMenuLayoutMenuItem (.nav-item-top / .nav-flyout-item /
                          // .sidebar-secondary-item) 専用の layout.css §「focus ring は共通ルールと別物」
                          // 規約対象で、 本 tabs raw slot は対象外なので 3px Focus.clarity へ揃える。
                          outline: 'none',
                          boxShadow:
                            '0 0 0 3px var(--chakra-colors-Focus-clarity)',
                        }}
                        _selected={{
                          backgroundColor: 'Surface.infoLow',
                          color: 'Text.accentPrimary',
                          fontWeight: 'semibold',
                          // recipe 選択時の下線 (borderBottomWidth 2px + marginBottom
                          // -1px + paddingBottom 3px + Border.ultraHighEmphasis) を
                          // 打ち消す
                          borderColor: 'transparent',
                          borderBottomWidth: 0,
                          marginBottom: 0,
                          paddingBottom: '6px',
                        }}
                        whiteSpace="normal"
                        css={{
                          // (a) recipe base fontWeight 600 の打ち消し
                          fontWeight: 400,
                          lineHeight: 1.35,
                          // (b) recipe `& > span` の border-frame + padding + color を
                          // 全て無効化し、 span を透明 pass-through にする
                          '& > span': {
                            border: 'none',
                            borderRadius: 0,
                            padding: 0,
                            color: 'inherit',
                            backgroundColor: 'transparent',
                          },
                          // (d) recipe `_hover > span` を打ち消し (trigger 側の
                          // _hover だけを効かせる)
                          '&:hover > span': { backgroundColor: 'transparent' },
                          // (e) recipe `_selected > span` の Text.highEmphasis を
                          // 打ち消し、 trigger 側の accentPrimary を継承させる
                          '&[data-selected] > span': { color: 'inherit' },
                        }}
                        paddingX={2}
                        paddingY={1.5}
                        value={tabDef.key}
                      >
                        <span>{t(tabDef.labelKey)}</span>
                      </TabsTrigger>
                    ),
                  )}
                </Fragment>
              ))}
            </TabsList>
            {/* 右ペイン: 選択中タブのみマウントする (タブ切替でフィルタ・検索状態を
                リセットする既存挙動を維持。 旧 switch 実装と同型)。 内部で
                PageScrollArea (flex:1 + minHeight:0) を所有するタブは、 本 element が
                flex column + height 継承 (tabs recipe content._vertical: height:100%)
                であることを前提に残余高さへ広がる。 padding は design
                `.occto-content-pane` の var(--space-4) = 16px に合わせる。 */}
            <TabsContent
              flexDirection="column"
              flex="1 1 auto"
              display="flex"
              minWidth={0}
              minHeight={0}
              padding={4}
              value={tab}
            >
              {/* design/admin/occto.html:193-196 準拠の情報構造: 選択中マスタ名 (h2)
                  → 説明文 → 共通ツールバー。 見出しはサイドメニュー項目と同一
                  ソース (TABS[].labelKey) を流し込む (proto の `_updateTabTitle()` と
                  同型)。
                  TabsContent は gap を持たず、 タブ内の縦余白は各要素が持つ構造の
                  ため、 説明文とツールバーの間 16px は本 Box が持つ (proto の
                  `#occto-tab-desc { margin-bottom: var(--space-4) }` 相当)。
                  flexShrink=0: 下の PageScrollArea が残余高さを取る flex column の
                  中で、 見出し・説明文が縦に潰れないようにする。 */}
              <Box flexShrink={0} marginBottom={4}>
                <PageIntro
                  description={t(activeTab.descKey)}
                  heading={t(activeTab.labelKey)}
                />
              </Box>
              <ActiveTabComponent />
            </TabsContent>
          </TabsRoot>
        </Box>
      </PageBody>
    </PageLayout>
  )
}
